import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import { format } from "date-fns";
import { COLUMNS } from "./Column";
import DualPagination from "../../../../components/common/DualPagination";
import {
  FaMinusCircle,
  FaPlusCircle,
  FaSortAmountDown,
  FaSortAmountUpAlt
} from "react-icons/fa";
import {
  useTable,
  useRowSelect,
  useFilters,
  useSortBy,
  useGlobalFilter,
  usePagination,
  useExpanded
} from "react-table";
import "./rolemodaltable.scss";
import { useDispatch, useSelector } from "react-redux";
import { getUserRoleListThunk } from "../../../../redux/roleManagementSlice";
import TableSkeleton from "../../../skeletons/TableSkeleton";
import { useVisibleColumnCount } from "../../../../hooks/useVisibleColumnCount";
import { noDataPresent } from "../../../../locales/en";
import {
  ddMMMyyyy,
  PAGE_INDEX,
  PAGE_SIZE_25
} from "../../../../utils/constants/appConstants";

const renderSortIcon = (column) => {
  if (!column.isSorted) {
    return "";
  }
  return column.isSortedDesc ? <FaSortAmountDown /> : <FaSortAmountUpAlt />;
};

const RoleModalTable = ({ roleId }) => {
  const columns = useMemo(() => COLUMNS, []);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_25);
  const [data, setData] = useState([]);
  const [expandedRows, setExpandedRows] = useState([]);
  const dispatch = useDispatch();
  const { userListLoading } = useSelector((state) => state.roleManagement);
  const { headerRef, visibleColumnCount } = useVisibleColumnCount();

  const loadUsers = async (roleId, pageNumber, pageSize) => {
    if (!roleId) return;

    try {
      const payload = {
        id: roleId,
        params: {
          page: pageNumber,
          size: pageSize
        }
      };
      const response = await dispatch(getUserRoleListThunk(payload)).unwrap();

      setData(response.data.data);
      setTotalPages(response.data.pageInfo.totalPages);
    } catch {
      //handle error
    }
  };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    state,
    page,
    gotoPage,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    pageCount,
    pageOptions
  } = useTable(
    {
      columns,
      data,
      manualPagination: true,
      pageCount: totalPages,
      autoResetPage: false,
      initialState: { pageIndex: PAGE_INDEX, pageSize: PAGE_SIZE_25 }
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect
  );

  const { pageIndex } = state;

  useEffect(() => {
    loadUsers(roleId, pageIndex, pageSize);
  }, [roleId, pageIndex, pageSize]);

  const toggleRow = (rowId) => {
    setExpandedRows((prev) =>
      prev.includes(rowId)
        ? prev.filter((id) => id !== rowId)
        : [...prev, rowId]
    );
  };

  return (
    <>
      <table
        className="mt-2 mb-3 rolemodal-table bgless_table"
        {...getTableProps()}
      >
        <thead ref={headerRef}>
          {headerGroups.map((headerGroup) => {
            const { key, ...rest } = headerGroup.getHeaderGroupProps();
            return (
              <tr className="parentrow" {...rest} key={key}>
                <th />
                {headerGroup.headers.map((column) => (
                  <th
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    key={column.id}
                  >
                    <span>{renderSortIcon(column)}</span>
                    <span>{column.render("Header")}</span>
                  </th>
                ))}
              </tr>
            );
          })}
        </thead>
        <tbody {...getTableBodyProps()}>
          {userListLoading && (
            <TableSkeleton rows={10} columns={visibleColumnCount} />
          )}
          {!userListLoading && data.length === 0 && (
            <tr>
              <td colSpan={columns.length + 2} className="text-center">
                {noDataPresent}
              </td>
            </tr>
          )}
          {!userListLoading &&
            data.length > 0 &&
            page.map((row) => {
              prepareRow(row);
              const isExpanded = expandedRows.includes(row.id);
              return (
                <React.Fragment key={row.id}>
                  <tr className="parentrow" {...row.getRowProps()}>
                    <td className="expand-td">
                      <span
                        className="expandbtn cursor-pointer"
                        onClick={() => toggleRow(row.id)}
                      >
                        {isExpanded ? <FaMinusCircle /> : <FaPlusCircle />}
                      </span>
                    </td>
                    {row.cells.map((cell) => (
                      <td
                        key={cell.getCellProps().key}
                        {...cell.getCellProps()}
                      >
                        {cell.render("Cell")}
                      </td>
                    ))}
                  </tr>
                  {isExpanded && (
                    <tr className="childrow colexpand">
                      <td colSpan={columns.length + 2}>
                        <div className="tdbody">
                          {columns.map((column) => (
                            <span
                              key={column.accessor}
                              data-label={column.Header}
                            >
                              {column.accessor === "date"
                                ? format(
                                    new Date(row.original[column.accessor]),
                                    ddMMMyyyy
                                  )
                                : String(row.original[column.accessor])}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
        </tbody>
      </table>
      {data && data.length > 0 && (
        <DualPagination
          gotoPage={gotoPage}
          previousPage={previousPage}
          nextPage={nextPage}
          canPreviousPage={canPreviousPage}
          canNextPage={canNextPage}
          pageIndex={pageIndex}
          pageCount={pageCount}
          pageOptions={pageOptions}
          setPageSize={setPageSize}
          pageSize={pageSize}
          dataLength={data.length}
        />
      )}
    </>
  );
};

RoleModalTable.propTypes = {
  roleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
};

export default RoleModalTable;
